/**
 * Phase 24.4b Step 6 — top-3 alternatives dialog.
 *
 * Non-optimistic swap (S4 Round 3 Q5): user clicks alternative,
 * mutation fires server round-trip, success toast + invalidates the
 * meal-plan query so the new item replaces the row. Backend response
 * provides shopping_list_changes which can flash via toast.
 */
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSwapOptions,
  swapMealPlanItem,
  type SwapAlternative,
} from "@/api/meal_plan";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/stores/toast";
import { cn } from "@/lib/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  planId: number;
  itemId: number;
}

export function SwapDialog({ isOpen, onClose, planId, itemId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { show } = useToast();

  const optionsQ = useQuery({
    queryKey: ["meal-plan-swap-options", planId, itemId],
    queryFn: () => getSwapOptions(planId, itemId),
    enabled: isOpen,
    staleTime: 60_000,
  });

  const swapMut = useMutation({
    mutationFn: (recipeId: number) => swapMealPlanItem(planId, itemId, recipeId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["meal-plan-current"] });
      qc.invalidateQueries({ queryKey: ["meal-plan", planId] });
      qc.invalidateQueries({ queryKey: ["shopping-list"] });
      const changes = data.shopping_list_changes;
      show({
        message: t("meal_plan.swap_success", {
          created: changes.rows_created,
          preserved: changes.rows_preserved_state,
        }),
        variant: "success",
      });
      onClose();
    },
    onError: () => {
      show({ message: t("meal_plan.swap_failed"), variant: "error" });
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("meal_plan.swap_title")}
      maxWidth="max-w-xl"
    >
      <div data-testid="swap-dialog">
        {optionsQ.isLoading ? (
          <div className="space-y-2" data-testid="swap-loading">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : optionsQ.isError || !optionsQ.data ? (
          <p className="text-[13px] text-coral">{t("meal_plan.swap_load_failed")}</p>
        ) : optionsQ.data.alternatives.length === 0 ? (
          <p className="text-[13px] text-taupe">{t("meal_plan.swap_no_alternatives")}</p>
        ) : (
          <ul className="space-y-2">
            {optionsQ.data.alternatives.map((alt) => (
              <SwapAlternativeRow
                key={alt.recipe.id}
                alt={alt}
                onSelect={() => swapMut.mutate(alt.recipe.id)}
                disabled={swapMut.isPending}
              />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function SwapAlternativeRow({
  alt,
  onSelect,
  disabled,
}: {
  alt: SwapAlternative;
  onSelect: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const reasonText = alt.selection_reason
    ? t(
        alt.selection_reason.key,
        alt.selection_reason.params as Record<string, string | number>,
      )
    : null;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className={cn(
          "w-full rounded-lg border border-cream-border bg-cream p-3 text-left transition",
          disabled
            ? "opacity-50"
            : "hover:border-amber/50 hover:shadow-soft",
        )}
        data-testid={`swap-alternative-${alt.recipe.id}`}
      >
        <div className="text-[13px] font-medium text-navy-deep">
          {alt.recipe.name}
        </div>
        <div className="mt-1 text-[11px] text-taupe">
          <span className="metric-number text-navy-deep">
            {Math.round(alt.actual_kcal)}
          </span>{" "}
          {t("nutrition_page.kcal")} · {t("nutrition_page.p")}{" "}
          <span className="metric-number">{Math.round(alt.actual_protein_g)}</span> ·{" "}
          {t("nutrition_page.c")}{" "}
          <span className="metric-number">{Math.round(alt.actual_carbs_g)}</span> ·{" "}
          {t("nutrition_page.f")}{" "}
          <span className="metric-number">{Math.round(alt.actual_fat_g)}</span>
        </div>
        {reasonText ? (
          <div className="mt-1 text-[10px] italic text-taupe">{reasonText}</div>
        ) : null}
      </button>
    </li>
  );
}
